import React, { useState } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useSelector } from "react-redux";
import userRoleAuth from "../../utils/userRoleAuth/userRoleAuth";
import userRoles from "../../config/userRoles";
import { useNavigate } from "react-router-dom";
import { useViewEmployeePayrollProcessSummary } from "../../queries/payrollQuries";
import { getCurrentMonthNames } from "../../utils/FormatDate/formaDateInMonths";
import { PAYROLL_STATUS } from "../../redux/constants/payrollComponents";
const PayrollOverview = () => {
  const user = useSelector((state) => state.user.auth);
  const isHr = userRoleAuth(user, userRoles.HR);
  const navigate = useNavigate();

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const { data, isLoading } = useViewEmployeePayrollProcessSummary({
    month: selectedMonth,
    year: selectedYear,
  });

  const summary = data?.data?.[0] || {};
  const months = getCurrentMonthNames();
  const ALL_MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const handleYearChange = (date) => {
    if (date) {
      const selected = date.year();
      setSelectedYear(selected);
      if (selected === currentYear && selectedMonth > currentMonth) {
        setSelectedMonth(currentMonth);
      }
    }
  };

  const disableFutureYears = (current) =>
    current && current.year() > currentYear;

  const filteredMonths =
    selectedYear < currentYear ? ALL_MONTHS : ALL_MONTHS.slice(0, currentMonth);

  const isFiltered =
    selectedYear !== currentYear || selectedMonth !== currentMonth;

  const handleClearFilters = () => {
    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
  };

  return (
    <div className="w-full max-w-[1084px] mx-auto">
      <div className="px-10 pb-4 mt-8 flex flex-col">
        <h2 className="text-xl font-semibold text-gray-800">
          Employee Payroll Overview
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Manage all employee payroll proceesing
        </p>

        <div className="bg-white shadow-md border border-gray-100 rounded-lg p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Select Year:
              </label>
              <DatePicker
                onChange={handleYearChange}
                picker="year"
                value={dayjs(`${selectedYear}`, "YYYY")}
                allowClear={false}
                disabledDate={disableFutureYears}
                className="!border !border-gray-300 !rounded-md !px-3 !py-2 min-w-[120px] text-sm"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                Select Month:
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
              >
                {filteredMonths.map((month, index) => (
                  <option key={index} value={index + 1}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="text-sm text-gray-600 mt-4 flex items-center gap-2">
            Showing summary for:{" "}
            <strong>
              {months[selectedMonth - 1]} {selectedYear}
            </strong>
            {isFiltered && (
              <span
                className="text-blue-800 font-medium cursor-pointer"
                onClick={handleClearFilters}
              >
                Clear Filters
              </span>
            )}
          </p>
        </div>

        <div className="bg-white shadow-md border border-gray-100 rounded-lg my-6 pb-6">
          <div className="p-6">
            <h3 className="text-base text-gray-800 font-semibold pb-1.5">
              Payroll Process Summary
            </h3>
          </div>

          {isLoading ? (
            <div className="text-center text-sm text-gray-500 pb-6">
              Loading summary...
            </div>
          ) : (
            <div className="flex items-center justify-around">
              <SummaryItem
                label="Total Employees"
                value={summary?.total_employees}
                icon="/images/payroll-total-icon.svg"
                onViewDetails={() =>
                  navigate(
                    `/payroll/employee-payroll/review-and-generate?month=${selectedMonth}&year=${selectedYear}`,
                  )
                }
              />
              <SummaryItem
                label="Pending Payrolls"
                value={summary?.pending_payrolls}
                icon="/images/payroll-pending-icon.svg"
                onViewDetails={() =>
                  navigate(
                    `/payroll/employee-payroll/review-and-generate?month=${selectedMonth}&year=${selectedYear}&payroll_status=${PAYROLL_STATUS.PENDING}`,
                  )
                }
              />
              <SummaryItem
                label="Generated"
                value={summary?.generated_employees}
                icon="/images/payroll-generated-icon.svg"
                onViewDetails={() =>
                  navigate(
                    `/payroll/employee-payroll/review-and-publish?month=${selectedMonth}&year=${selectedYear}`,
                  )
                }
              />
              <SummaryItem
                label="Published"
                value={summary?.published_payrolls}
                icon="/images/payroll-published-icon.svg"
                onViewDetails={() =>
                  navigate(
                    `/payroll/employee-payroll/view-and-download?month=${selectedMonth}&year=${selectedYear}`,
                  )
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryItem = ({ label, value, icon, onViewDetails }) => (
  <div className="text-center flex flex-col items-center">
    <img src={icon} alt={label} />
    <p className="text-[#10B981] text-base font-semibold mt-2 mb-1">{label}</p>
    <p className="text-base font-semibold text-[#1F2937]">{value}</p>
    <button
      onClick={onViewDetails}
      className="text-[#3466E7] text-sm font-medium pt-4 underline"
    >
      view details
    </button>
  </div>
);

export default PayrollOverview;
